import MrclText from "../marcel/MrclText";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { formatKvalue, getCurrencySuffix } from "../../utils/numberUtils";
import { InfoBullType } from "../../types/infoBull";
import { InfoBull } from "../InfoBull";
import DeltaBadge from "../DeltaBadge";
import ProgressBar from "../ProgressBar";
import { COLORS } from "../../utils/colors";
import useBreakPoint from "../../utils/useBreakPoint";

interface CaCardProps {
  title: string;
  value: number;
  target?: number;
  rate: number;
  infoBull: InfoBullType;
  showInfoBull?: boolean;
  currency?: "K€" | "€" | "count";
  showObjective?: boolean;
}

const CaCard: React.FC<CaCardProps> = ({ title, value, target = 0, rate, infoBull, showInfoBull = true, currency = "K€", showObjective = true }) => {
  const isBreak = useBreakPoint();
  const diff = value - target;
  const objectiveColor = diff >= 0 ? COLORS.green : COLORS.red;
  const { t } = useTranslation();
  const objectiveSection = useCallback( () =>
      <>
        <ProgressBar target={target} value={value} />
        <CTextCustom size={"s"} bold $color={objectiveColor}>
          {`${ diff > 0 ? `+${formatKvalue(diff, "")}` : formatKvalue(diff, "")}${getCurrencySuffix(diff)} `}
          <MrclText style={{color: '#696969', fontWeight: 400, paddingLeft: '3px', paddingRight: '3px', fontSize: '12px'}}>
            {isBreak !== "desktop" ? t('common.short_sur_objectif_de') : t('common.sur_objectif_de')}
          </MrclText>
          {target && !isNaN(target) ?  <span style={{color: '#454545'}}>{`${formatKvalue(target, "")}${getCurrencySuffix(target)}`}</span> : ("-")}
        </CTextCustom>
      </>
  , [diff, objectiveColor, target, t, value, isBreak]);

  return (
        <Card>
          <HeaderContainer>
            <CTextHeader>{title}</CTextHeader>
            {showInfoBull && (
              <InfoBullWrapper>
                <InfoBull
                    description={title as string}
                    lastUpdated={infoBull?.lastUpdated}
                    loading={infoBull?.loading}
                    isUnavailable={infoBull?.isUnavailable}
                    error={infoBull?.error}
                    onOpenTracking={() => {}}
                />
              </InfoBullWrapper>
            )}
          </HeaderContainer>
          <Content>
          <CTextValue>
            {value && !isNaN(value) ? (
              currency === "€"
                ? `${new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} €`
                : currency === "count"
                ? `${formatKvalue(value / 1000, "")}${getCurrencySuffix(value / 1000).replace("€", "")}`
                : `${formatKvalue(value, "")}${getCurrencySuffix(value)}`
            ) : "-"}
          </CTextValue>
          <Variation>
            {rate != null && !isNaN(rate) ? (
              <>
                <DeltaBadge value={rate} />
                <span className="vs">vs {t("commercial.n_1")}</span>
              </>
            ) : (
              "-"
            )}
          </Variation>
           {showObjective && !!target && objectiveSection()}
           </Content>
        </Card>
  );
};

export default CaCard;

const InfoBullWrapper = styled.div`
  margin-right: 4px;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.05);
  padding: 16px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;  
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: transform 0.15s ease-in-out;

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    padding: 8px;
  }
`;

const CTextHeader = styled(MrclText)`
  color: #121212;
  font-size: var(--font-size-body-m, 16px);
  font-weight: 700;
  margin-bottom: 4px;

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    font-size: 11px;
  }
`;

const CTextValue = styled(MrclText)`
  color: #0c1021;
  font-size: 24px !important;
  font-weight: 700;
  margin: 0;
  line-height: 1.1;
  text-align: left;

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    font-size: 18px !important;
  }
`;

const Variation = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 4px;

  .vs {
    color: #9a9da6;
    font-size: 12px;
    line-height: 1;
  }

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    gap: 4px;
    margin-bottom: 2px;

    .vs {
      font-size: 10px;
    }
  }
`;

const CTextCustom = styled(MrclText)<{ $color: string, fontSize?: string }>`
  font-size: ${(props) => props.fontSize || "12px"} !important;
  color: ${(props) => props.$color};

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    font-size: 10px !important;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 6px;
  margin-top: 10px;

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    gap: 4px;
    margin-top: 4px;
  }
`;
